import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaAsterisk, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getReadableErrorMessage } from '../../utils/utils';
import { db } from '../../config/firebase';
import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import LoaderSpinner from '../common/CustomLoader';

export default function ContactUsPage() {
  const faqs = [
    { question: "How do I place an order?", answer: "To place an order, simply add the items to your cart and checkout using Cash on Delivery. Make sure your shipping information is accurate." },
    { question: "What payment methods are accepted?", answer: "Currently, we only accept Cash on Delivery." },
    { question: "How do I track my order?", answer: "After placing your order, you can track its status from the orders menu in the account screen." },
    { question: "Can I return or exchange a product?", answer: "Returns and exchanges are subject to review. Please refer to our Returns Policy for detailed instructions and conditions." },
    { question: "How do I contact customer support?", answer: "You can reach our customer support via the in-app support." },
    { question: "What is the estimated delivery time?", answer: "Delivery times vary by location. We strive to process and deliver your order as quickly as possible." },
    { question: "Is my personal data safe?", answer: "We take your privacy seriously and implement industry-standard security measures to protect your personal data." },
    { question: "How can I update my account information?", answer: "You can update your account details, including your shipping address and contact information, directly in the app's account settings." },
    { question: "How do I delete my account?", answer: "You can delete your account by navigating to Account Settings in the app, selecting Profile, and choosing Delete Account. After deletion, your account will be in a 90-day recovery period before permanent removal." },
    { question: "How do I delete my data?", answer: "You have full control over your data. In the app, go to Account Settings and choose what data you want to remove, such as order history, saved addresses, or payment details." },

  ];

  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };


  // Contact us
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");

  const [message, setMessage] = useState("");

  const [validationError, setValidationError] = useState({});

  // Validation
  const validateFields = async () => {
    const errors = {};
    // Email Validation with Regex
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (email.trim() === "") {
      errors.email = "Email is Required";
    } else if (!emailRegex.test(email)) {
      errors.email = "Please enter a valid email address";
    }

    if (subject.trim() === "") {
      errors.subject = "Subject is Required";
    }

    if (message.trim() === "") {
      errors.message = "Message is Required";
    }


    setValidationError(errors);
    console.log(errors);
    return Object.keys(errors).length === 0; // Returns true if no errors
  }

  const clearFields = () => {
    setEmail("");
    setSubject("");
    setMessage("");
  }

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const isFieldsValid = await validateFields(); // Await the validation
    if (!isFieldsValid) return; // Stop if validation fails
    setIsLoading(true);
    try {

      var contactDoc = {
        createdAt: Timestamp.now(),
        email: email,
        markAsRead: false,
        message: message,
        subject: subject,
      };
      const contactRef = doc(collection(db, "contactMessages")); // Use productId as the document ID
      await setDoc(contactRef, contactDoc);

      toast.success("Message Sent successfully!", {
        position: "top-right",
        autoClose: 3000, // Auto close after 3 seconds
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      clearFields();
    }
    catch (err) {
      console.error("Error Sending Message:", err.message);
      var errorMsg = getReadableErrorMessage(err.code || err.message);
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 3000, // Auto close after 3 seconds
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
    finally {
      setIsLoading(false); // Stop loading
    }
  }

  return (
    <div className="w-full p-4">
      {isLoading && <LoaderSpinner />}
      <h1 className="text-3xl font-bold mb-6">Help Center</h1>


      <section className="flex flex-col md:flex-row gap-8">
        {/* FAQs Section */}
        <div className='md:w-1/2'>
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-300">
                <button
                  className="w-full text-left flex justify-between items-center py-3 text-lg font-semibold text-dark-text-color focus:outline-none"
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                  <span>{openIndex === index ? <FaChevronUp /> : <FaChevronDown />}</span>
                </button>

                {/* Animated FAQ Answer */}
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={openIndex === index ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <p className="text-light-text-color p-3">{faq.answer}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
        <form className='md:w-1/2' onSubmit={handleSendMessage}>
          <h2 className="text-2xl font-bold mb-4">Contact Us</h2>

          <div className='grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4 mt-3 mb-1'>

            {/* Email */}
            <div style={{ marginBottom: "1rem" }}>
              <div className="flex flex-col items-start mb-1">
                <span className="flex items-center">
                  Email  <sup className="text-red-icon-color text-xs ml-1"><FaAsterisk size={8} /></sup>
                </span>
              </div>

              <input
                className="w-full px-4 py-4 rounded-lg font-medium bg-input-bg border border-input-border placeholder-input-placeholder text-sm focus:outline-none focus:border-input-focus-border focus:bg-input-focus-bg mb-1 mt-1"
                type="email"
                value={email}
                placeholder={`Email`}
                required={true}
                onChange={(e) => setEmail(e.target.value)}
              />

              {validationError.email && <p className="text-error-color text-sm mt-1 mb-1 mx-3">{validationError.email}</p>}

            </div>

            {/* Subjec */}

            <div style={{ marginBottom: "1rem" }}>
              <div className="flex flex-col items-start mb-1">
                <span className="flex items-center">
                  Subject  <sup className="text-red-icon-color text-xs ml-1"><FaAsterisk size={8} /></sup>
                </span>
              </div>

              <input
                className="w-full px-4 py-4 rounded-lg font-medium bg-input-bg border border-input-border placeholder-input-placeholder text-sm focus:outline-none focus:border-input-focus-border focus:bg-input-focus-bg mb-1 mt-1"
                type="text"
                value={subject}
                placeholder={`Subject`}
                required={true}
                onChange={(e) => setSubject(e.target.value)}
              />

              {validationError.subject && <p className="text-error-color text-sm mt-1 mb-1 mx-3">{validationError.subject}</p>}

            </div>
          </div>


          <div style={{ marginBottom: "1rem" }}>
            <div className="flex flex-col items-start mb-1">
              <span className="flex items-center">
                Message  <sup className="text-red-icon-color text-xs ml-1"><FaAsterisk size={8} /></sup>
              </span>
            </div>

            <textarea
              className="w-full px-4 py-4 rounded-lg font-medium bg-input-bg border border-input-border placeholder-input-placeholder text-sm focus:outline-none focus:border-input-focus-border focus:bg-input-focus-bg mb-1 mt-1"
              type="text"
              value={message}
              placeholder={`Message`}
              required={true}
              rows={8}
              onChange={(e) => setMessage(e.target.value)}
            />

            {validationError.message && <p className="text-error-color text-sm mt-1 mb-1 mx-3">{validationError.message}</p>}

          </div>


          {/* Submit Button */}
          <div className="flex justify-end mt-5">
            <button
              type='submit'
              className="p-6 mt-5 tracking-wide font-semibold bg-light-green text-light-text-color py-4 rounded-lg hover:bg-bright-green hover:text-white transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none"
              onClick={(e) => handleSendMessage(e)}
            >
              Send
            </button>
          </div>

        </form>
      </section>


    </div>
  );
}
